import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { connectRedux } from '../../helpers/connectRedux'
import { Button, InfoNote, Spinner, ValidatedInput } from '../../components'
import CountryPickerModal from '../CountryPickerModal'
import countries from '../../countries.json'
import { isEmailValid, isTelephoneValid } from '../../helpers'
import { appsRecoverPasswordSelected, defaultPhoneCountryCode } from '../../helpers/app'
import { ICountry } from '../../models/country'
import { LiteralsService } from '../../services'
import {
  requestRecoverCodeAction,
  requestRecoverCodeFailedAction,
  setRecoverPasswordCallingCodeAction,
  setRecoverPasswordCountryAction,
  setRecoverPasswordEmailAction,
  setRecoverPasswordPhoneAction
} from '../../store/actions'
import { IStoreState } from '../../store/states'

const isPhoneRecovery = appsRecoverPasswordSelected?.username === 'phone'

interface IProps extends DispatchProp<AnyAction> {
  language: string
  email: string
  phone: string
  error: string
  isLoading: boolean
  country?: ICountry
}

/**
 * First step of recovering a password — who is asking for the code — shared by the page and the
 * guest prompt. Where it goes afterwards is the recover saga's business.
 */
class RecoverPasswordRequestCodeForm extends PureComponent<IProps> {
  constructor(props: IProps) {
    super(props)
    if (isPhoneRecovery && !props.country) {
      const defaultCountry = countries.find(country => country.code === defaultPhoneCountryCode)
      if (defaultCountry) {
        props.dispatch(setRecoverPasswordCountryAction(defaultCountry))
        props.dispatch(setRecoverPasswordCallingCodeAction(defaultCountry.dial_code))
      }
    }
  }

  private get buttonIsDisabled(): boolean {
    const { email, phone, isLoading, error } = this.props

    if (isPhoneRecovery) return !isTelephoneValid(phone) || isLoading || !!error
    return !isEmailValid(email) || isLoading || !!error
  }

  private get noUserError(): ReactNode {
    const error = this.props.error
    if (!error) return null
    return (
      <div className="error-container">
        <span className="error">
          { LiteralsService.get(error as any, true) }
        </span>
      </div>
    )
  }

  private get recoverButton(): ReactNode {
    if (this.props.isLoading) return <div className="loading"><Spinner /></div>
    return (
      <Button
        title={ LiteralsService.get('next') }
        onPress={ this.handleValidatedButtonPress }
        filled
        upper
        isDisabled={ this.buttonIsDisabled }
      />
    )
  }

  public render(): ReactNode {
    const { email, phone, error, country } = this.props

    return (
      <>
        <section>
          {
            isPhoneRecovery ? (
              <ValidatedInput
                name="phone"
                phone
                label={ LiteralsService.get('phone', true) }
                value={ phone }
                country={ country }
                onChange={ this.onPhoneChange }
                error={ !isTelephoneValid(phone) || !!error }
              />
            ) : (
              <ValidatedInput
                name="email"
                label={ LiteralsService.get('email', true) }
                value={ email }
                onChange={ this.onEmailChange }
                error={ !isEmailValid(email) || !!error }
              />
            )
          }
          { this.noUserError }
          <InfoNote literal="willSendVerificationCode" />
        </section>
        { isPhoneRecovery && <CountryPickerModal selectCountry={ this.onCountryChange } selectedCountry={ this.props.country } /> }
        <section className="actions">
          { this.recoverButton }
        </section>
      </>
    )
  }

  private onEmailChange = (email: string): void => {
    const { dispatch, error } = this.props
    if (error) dispatch(requestRecoverCodeFailedAction(''))
    dispatch(setRecoverPasswordEmailAction(email))
  }

  private onPhoneChange = (phone: string): void => {
    const { dispatch, error } = this.props
    if (error) dispatch(requestRecoverCodeFailedAction(''))
    dispatch(setRecoverPasswordPhoneAction(phone))
  }

  private onCountryChange = (country: ICountry): void => {
    const { dispatch } = this.props
    dispatch(setRecoverPasswordCountryAction(country))
    dispatch(setRecoverPasswordCallingCodeAction(country.dial_code))
  }

  private handleValidatedButtonPress = (): void => {
    this.props.dispatch(requestRecoverCodeAction())
  }
}

const mapStateToProps = ({ recoverPassword, configuration }: IStoreState) => ({
  // Redraws the literals when the language selector changes the language.
  language: configuration.language,
  email: recoverPassword.email,
  phone: recoverPassword.phone,
  error: recoverPassword.error,
  isLoading: recoverPassword.isLoading,
  country: recoverPassword.country
})

export default connectRedux(mapStateToProps, RecoverPasswordRequestCodeForm) as React.ComponentType<{}>
